import React, { useState } from 'react';
import { X, Copy, Check, Share2 } from 'lucide-react';
import { FamilyMember } from '../types';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: FamilyMember | { name: string; id: string; familyId?: string };
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, currentUser }) => {
  const [copied, setCopied] = useState(false);
  
  if (!isOpen) return null;

  // Get familyId from currentUser
  const familyId = (currentUser as FamilyMember).familyId || (currentUser as any).familyId;
  
  // Create invite link with familyId parameter
  const inviteLink = familyId
    ? `https://famitry.in?familyId=${encodeURIComponent(familyId)}`
    : `https://famitry.in`;
  
  const message = `Hey! I'm building our family tree on Famitry. Join me to see old photos and our history! ${inviteLink}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        
        {/* Header */}
        <div className="bg-indigo-600 p-6 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
          
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-white/30">
            <Share2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-black text-white">Grow the Tree</h2>
          <p className="text-indigo-100 text-sm mt-1">Invite family to unlock more history.</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Copy Link Section */}
          <div className="flex gap-2">
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500 truncate font-mono">
              {inviteLink}
            </div>
            <button 
              onClick={handleCopy}
              className={`px-3 py-3 rounded-xl font-bold transition-all flex items-center justify-center flex-shrink-0 ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 px-4">
            You earn <span className="text-amber-500 font-bold">+100 XP</span> for every family member who joins using your code.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;